import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | pending | failed
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") setStatus("paid");
        else if (["failed", "expired"].includes(data.payment_status)) setStatus("failed");
        else if (tries < 8) {
          setTries(t => t + 1);
          setTimeout(poll, 2000);
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="fade-up flex items-center justify-center py-16">
      <div className="max-w-md w-full p-8 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-[#C7F500] mx-auto animate-spin" />
            <h1 className="font-heading text-2xl font-bold mt-4">Confirming your payment…</h1>
            <p className="text-sm text-[#AAB7C7] mt-2">Hang tight for a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#C7F500] mx-auto" />
            <h1 className="font-heading text-2xl font-bold mt-4" data-testid="billing-success-title">You&apos;re upgraded!</h1>
            <p className="text-sm text-[#AAB7C7] mt-2">Your league now has the new player limit. Nice work.</p>
            <button onClick={() => nav("/app/admin")}
              data-testid="back-to-admin"
              className="mt-6 w-full py-3 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] font-semibold">
              Back to Admin
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-heading text-2xl font-bold">Almost there…</h1>
            <p className="text-sm text-[#AAB7C7] mt-2">Payment is still processing. Your plan will update within a minute.</p>
            <Link to="/app/admin" className="mt-6 inline-block text-[#C7F500] hover:underline">Back to Admin</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <h1 className="font-heading text-2xl font-bold">Payment not completed</h1>
            <p className="text-sm text-[#AAB7C7] mt-2">Something went wrong or the session expired. No charge was made.</p>
            <Link to="/app/admin" className="mt-6 inline-block text-[#C7F500] hover:underline">Back to Admin</Link>
          </>
        )}
      </div>
    </div>
  );
}
